// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import {
	computed,
	ref,
	provide,
	type VNodeRef,
	reactive,
	watch,
	onMounted,
	onBeforeUnmount,
} from 'vue'
import { useUrlSearchParams } from '@vueuse/core'
import {
	checkProductSharingEnable,
	productSharingPostPerPage,
	productSharingPostColumn,
} from '@/legacy/ts/productSharing'
import { isMobile } from '@/legacy/ts/checkDevice'
import { useBreakpoints } from '@/lib/utils'
import useSnackBar from '@/legacy/ts/useSnackBar'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import usePostCard from '@/legacy/ts/usePostCard'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import ProductSharingSearchBar from '@/legacy/components/ProductSharingSearchBar.vue'
import ProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import ProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import BackButton from '@/components/BackButton.vue'
import hybridPage from '@/lib/hybridPage'

hybridPage()

createApp({
	name: 'Product-Sharing-Search',
	components: {
		BsSnackBar,
		BsDialogAction,
		ProductSharingSearchBar,
		ProductSharingPostList,
		ProductSharingPostCard,
		BackButton,
	},
	setup() {
		const { postCardTextInfo } = window
		const { postListTextInfo } = window
		const { searchbarTextInfo } = window.pageContent
		const { isShow, messages, dismiss, addMessage } = useSnackBar()
		const postListRef = ref<VNodeRef | null>(null)
		const {
			postDetailCurrent,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			DialogType,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			localCurrencyInfo,
			interestsList,
			hideDialogPostDetail,
			setPostActions,
		} = usePostCard(postListRef)
		const { isTabletLandscape, isMedium, isLarge } = useBreakpoints()
		const queryParams = useUrlSearchParams('history')
		provide('addMessage', addMessage)

		const isShowClose = ref(false)
		const backToTopObserveElement = ref(null)
		const listObserver = ref<IntersectionObserver | null>(null)
		const meta = reactive({
			query: '',
			totalCount: 0,
		})
		const isLoading = ref(false)
		const productSharingEnable = checkProductSharingEnable()
		const columnCount = computed(() => {
			if (isLarge.value) return productSharingPostColumn.SizeLG
			if (isTabletLandscape.value) return productSharingPostColumn.SizeMDL
			if (isMedium.value) return productSharingPostColumn.SizeMDP
			return productSharingPostColumn.SizeSM
		})
		const searchKeyword = computed(() => {
			return queryParams.q ? queryParams.q : ''
		})

		const hashtagKeyword = computed(() => {
			return queryParams.hashtag ? queryParams.hashtag : ''
		})

		const queryString = computed(() => {
			return meta.query
		})

		const queryStringType = computed(() => {
			return hashtagKeyword.value ? 'hashtag' : 'q'
		})

		function updateMeta(data: {
			count: number
			query: {
				q?: string
				hashtag?: string
			}
			total_count: number
		}) {
			meta.query =
				(hashtagKeyword.value ? data.query.hashtag : data.query.q) || ''
			meta.totalCount = data.total_count
		}

		onMounted(() => {
			initialBackTopButtonObserver()
		})

		onBeforeUnmount(() => {
			clearBackTopButtonObserver()
		})

		function scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		}

		function initialBackTopButtonObserver() {
			listObserver.value = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							isShowClose.value = false
						} else {
							isShowClose.value = true
						}
					})
				},
				{
					threshold: 1,
				},
			)

			watch(
				backToTopObserveElement,
				() => {
					if (!backToTopObserveElement.value) return
					if (listObserver.value) {
						listObserver.value.observe(backToTopObserveElement.value)
					}
				},
				{
					immediate: true,
				},
			)
		}

		function clearBackTopButtonObserver() {
			if (listObserver.value) {
				listObserver.value.disconnect()
			}
		}

		return {
			isShowClose,
			backToTopObserveElement,
			scrollToTop,
			productSharingEnable,
			isLoading,
			searchbarTextInfo,
			queryString,
			queryStringType,
			productSharingPostPerPage,
			postCardTextInfo,
			postListTextInfo,
			isShow,
			messages,
			meta,
			postDetailCurrent,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			DialogType,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			localCurrencyInfo,
			interestsList,
			columnCount,
			searchKeyword,
			hashtagKeyword,
			postListRef,
			dismiss,
			updateMeta,
			hideDialogPostDetail,
			setPostActions,
			isMobile,
		}
	},
}).mount('#bsMain')
